import type { TargetAndTransition, Transition } from 'framer-motion';

export type EntranceKind = 'draw' | 'reveal' | 'growX' | 'growY' | 'fade' | 'sweep';

export type Entrance = {
  initial: TargetAndTransition | false;
  animate: TargetAndTransition;
  transition: Transition;
};

type Options = {
  /** True under `prefers-reduced-motion: reduce`: the mark renders in its final state. */
  reduced: boolean;
  /** Seconds before it starts; each kind has its own default. */
  delay?: number;
  /** Final width of a `sweep` curtain. */
  width?: number;
  /** Seconds the entrance lasts; each kind has its own default. */
  duration?: number;
};

const DRAW_EASE = [0.4, 0, 0.2, 1] as const;
const GROW_EASE = [0.22, 1, 0.36, 1] as const;
const SWEEP_EASE = [0.45, 0, 0.25, 1] as const;

// The landing's motion vocabulary: a line draws itself, a bar grows from its base,
// a label fades in once the marks settle, and a curtain uncovers a flow left to right.
const KINDS: Record<
  EntranceKind,
  (width: number) => { from: TargetAndTransition; to: TargetAndTransition; transition: Transition }
> = {
  draw: () => ({
    from: { pathLength: 0 },
    to: { pathLength: 1 },
    transition: { duration: 1.4, delay: 0.2, ease: DRAW_EASE },
  }),
  // A dashed or dotted line cannot draw by its length: animating pathLength rewrites
  // stroke-dasharray, so the dash pattern would be lost. It is uncovered left to right instead.
  reveal: () => ({
    from: { clipPath: 'inset(0% 100% 0% 0%)' },
    to: { clipPath: 'inset(0% 0% 0% 0%)' },
    transition: { duration: 1.4, delay: 0.2, ease: DRAW_EASE },
  }),
  growX: () => ({
    from: { scaleX: 0 },
    to: { scaleX: 1 },
    transition: { duration: 0.6, delay: 0, ease: GROW_EASE },
  }),
  growY: () => ({
    from: { scaleY: 0 },
    to: { scaleY: 1 },
    transition: { duration: 0.6, delay: 0, ease: GROW_EASE },
  }),
  fade: () => ({
    from: { opacity: 0 },
    to: { opacity: 1 },
    transition: { duration: 0.6, delay: 0.9, ease: 'easeOut' },
  }),
  sweep: (width) => ({
    from: { width: 0 },
    to: { width },
    transition: { duration: 0.8, delay: 0, ease: SWEEP_EASE },
  }),
};

/** framer-motion props for a chart mark's one-time entrance. */
export function entrance(
  kind: EntranceKind,
  { reduced, delay, width = 0, duration }: Options,
): Entrance {
  const { from, to, transition } = KINDS[kind](width);
  if (reduced) return { initial: false, animate: to, transition: { duration: 0 } };
  return {
    initial: from,
    animate: to,
    transition: {
      ...transition,
      ...(delay === undefined ? {} : { delay }),
      ...(duration === undefined ? {} : { duration }),
    },
  };
}
